import type {
  StyleElevationRequest,
  StyleExtrusionRequest,
  StyleLabelsRequest,
  StyleLayerRequest,
  StylePointSymbolRequest,
  StyleSymbologyRequest,
  StyleSymbolRequest,
} from './map-tool-types'

type Obj = Record<string, unknown>
type Part = 'symbology' | 'labels' | 'extrusion' | 'elevation' | 'pointSymbol'

const MODES = ['simple', 'categorized', 'graduated', 'expression'] as const
const METHODS = ['natural_breaks', 'quantile', 'equal_interval', 'standard_deviation'] as const
const OPERATORS = ['==', '!=', '>', '<', '>=', '<=', 'in', 'contains', 'between'] as const
const RULE_LIST_MAX = 50
const PLACEMENTS = ['above-center', 'above-right', 'above-left', 'center-center', 'below-center'] as const
const COLLISIONS = ['exclude', 'allow'] as const
const UNITS = ['meters', 'feet'] as const
const ELEVATION_MODES = ['on-the-ground', 'relative-to-ground', 'relative-to-scene', 'absolute-height'] as const
const MAX_RULES = 20
const POINT_KINDS = ['circle', 'icon', 'model'] as const
const PLACEMENT_3D = ['on-ground', 'relative'] as const

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
/** A field name, or null to clear it (null or an empty string). */
const clearable = (v: unknown): string | null | undefined =>
  v === null || (typeof v === 'string' && v.trim() === '') ? null : str(v)
const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined)
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined =>
  typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : undefined

/** Copies only the entries whose value is defined, so absent properties stay absent. */
function defined<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T
}

const SYMBOL_PROBLEM =
  'symbology.symbol must be an object with fill_color, stroke_color, stroke_width, opacity, point_radius'

/** A problem message naming each present value that is not a finite number. */
function numberProblem(raw: Obj, keys: readonly string[], prefix: string): string | undefined {
  const bad = keys.filter(k => raw[k] !== undefined && num(raw[k]) === undefined)

  return bad.length > 0 ? `${bad.map(k => `${prefix}${k}`).join(', ')} must be a finite number` : undefined
}

/** A problem message for a present value outside its enum. */
function enumProblem(raw: Obj, key: string, allowed: readonly string[], prefix: string): string | undefined {
  return raw[key] !== undefined && oneOf(allowed, raw[key]) === undefined
    ? `${prefix}${key} must be one of: ${allowed.join(', ')}`
    : undefined
}

const firstProblem = (...problems: Array<string | undefined>) => problems.find(p => p !== undefined)

function readSymbol(raw: unknown): { symbol?: StyleSymbolRequest; problem?: string } {
  if (raw === undefined) return {}
  if (!isObj(raw)) return { problem: SYMBOL_PROBLEM }
  const colorBad = ['fill_color', 'stroke_color'].some(k => raw[k] !== undefined && str(raw[k]) === undefined)

  if (colorBad || numberProblem(raw, ['stroke_width', 'opacity', 'point_radius'], 'symbol.')) {
    return { problem: SYMBOL_PROBLEM }
  }

  const symbol = defined({
    fillColor: str(raw.fill_color),
    strokeColor: str(raw.stroke_color),
    strokeWidth: num(raw.stroke_width),
    opacity: num(raw.opacity),
    pointRadius: num(raw.point_radius),
  })

  return Object.keys(symbol).length > 0 ? { symbol } : {}
}

function readRule(raw: unknown, i: number) {
  const at = `rules[${i}]: `

  if (!isObj(raw)) return { error: `${at}each rule needs field, operator, value and color` }
  const field = str(raw.field)
  const color = str(raw.color)
  const scalar = (v: unknown): v is string | number => typeof v === 'string' || num(v) !== undefined
  const list = Array.isArray(raw.value) && raw.value.length <= RULE_LIST_MAX && raw.value.every(scalar)
  const value = scalar(raw.value) || list ? (raw.value as string | number | Array<string | number>) : undefined

  if (!field || !color || value === undefined || raw.operator === undefined) {
    return {
      error: `${at}each rule needs field, operator, value (string, finite number, or a list of up to ${RULE_LIST_MAX}) and color`,
    }
  }

  const operator = oneOf(OPERATORS, raw.operator)
  if (!operator) return { error: `${at}operator must be one of ${OPERATORS.join(' ')}` }

  return { rule: defined({ field, operator, value, color, label: str(raw.label) }) }
}

function readSymbology(raw: unknown): { value?: StyleSymbologyRequest; note?: string } {
  if (!isObj(raw)) return { note: 'symbology must be an object' }
  const mode = oneOf(MODES, raw.mode)
  if (!mode) return { note: `symbology.mode must be one of ${MODES.join(', ')}` }
  const { symbol, problem } = readSymbol(raw.symbol)

  if (problem) return { note: problem }
  const withSymbol = symbol ? { symbol } : {}

  if (mode === 'simple') return { value: { mode, ...withSymbol } }

  if (mode === 'expression') {
    if (!Array.isArray(raw.rules)) return { note: 'expression symbology needs a rules array' }
    if (raw.rules.length > MAX_RULES) return { note: `expression symbology takes at most ${MAX_RULES} rules` }
    const rules = []

    for (const [i, r] of raw.rules.entries()) {
      const read = readRule(r, i)
      if (read.error || !read.rule) return { note: read.error }
      rules.push(read.rule)
    }

    const defaultColor = str(raw.default_color)

    return { value: { mode, rules, ...(defaultColor ? { defaultColor } : {}), ...withSymbol } }
  }

  const field = str(raw.field)?.trim()
  if (!field) return { note: `${mode} symbology needs a field` }

  if (mode === 'categorized') {
    let note: string | undefined
    let colors: Record<string, string> | undefined

    if (raw.colors !== undefined) {
      if (isObj(raw.colors)) {
        colors = {}
        for (const [k, v] of Object.entries(raw.colors)) {
          if (typeof v !== 'string') return { note: 'symbology.colors values must be #rrggbb strings' }
          colors[k] = v
        }
      } else note = 'colors must be an object of value to color'
    }

    return { value: { mode, field, ...(colors ? { colors } : {}), ...withSymbol }, note }
  }

  const graduatedProblem = firstProblem(
    enumProblem(raw, 'method', METHODS, 'symbology.'),
    numberProblem(raw, ['classes'], 'symbology.'),
  )

  if (graduatedProblem) return { note: graduatedProblem }

  return {
    value: {
      mode,
      field,
      ...defined({ method: oneOf(METHODS, raw.method), classes: num(raw.classes), colorRamp: str(raw.color_ramp) }),
      ...withSymbol,
    },
  }
}

function labelsProblem(raw: Obj): string | undefined {
  return firstProblem(
    enumProblem(raw, 'placement', PLACEMENTS, 'labels.'),
    enumProblem(raw, 'collision', COLLISIONS, 'labels.'),
    numberProblem(raw, ['font_size', 'halo_size', 'min_scale', 'max_scale'], 'labels.'),
  )
}

const extrusionProblem = (raw: Obj) => enumProblem(raw, 'unit', UNITS, 'extrusion.')

function elevationProblem(raw: Obj): string | undefined {
  return firstProblem(
    enumProblem(raw, 'mode', ELEVATION_MODES, 'elevation.'),
    enumProblem(raw, 'unit', UNITS, 'elevation.'),
    numberProblem(raw, ['offset'], 'elevation.'),
  )
}

function readLabels(raw: Obj): StyleLabelsRequest {
  return defined({
    enabled: bool(raw.enabled),
    field: str(raw.field),
    arcade: str(raw.arcade),
    fontFamily: str(raw.font_family),
    fontSize: num(raw.font_size),
    bold: bool(raw.bold),
    italic: bool(raw.italic),
    color: str(raw.color),
    haloColor: str(raw.halo_color),
    haloSize: num(raw.halo_size),
    placement: oneOf(PLACEMENTS, raw.placement),
    collision: oneOf(COLLISIONS, raw.collision),
    minScale: num(raw.min_scale),
    maxScale: num(raw.max_scale),
  })
}

function readExtrusion(raw: Obj): StyleExtrusionRequest {
  return defined({
    remove: bool(raw.remove),
    heightField: str(raw.height_field),
    baseField: clearable(raw.base_field),
    unit: oneOf(UNITS, raw.unit),
  })
}

function readElevation(raw: Obj): StyleElevationRequest {
  return defined({
    remove: bool(raw.remove),
    mode: oneOf(ELEVATION_MODES, raw.mode),
    offset: num(raw.offset),
    offsetField: clearable(raw.offset_field),
    unit: oneOf(UNITS, raw.unit),
  })
}

function pointSymbolProblem(raw: Obj): string | undefined {
  const webStyle = raw.web_style
  const webStyleBad =
    webStyle !== undefined && (!isObj(webStyle) || str(webStyle.style_name) === undefined || str(webStyle.name) === undefined)

  return firstProblem(
    enumProblem(raw, 'kind', POINT_KINDS, 'point_symbol.'),
    enumProblem(raw, 'placement', PLACEMENT_3D, 'point_symbol.'),
    enumProblem(raw, 'height_unit', UNITS, 'point_symbol.'),
    numberProblem(raw, ['size', 'heading'], 'point_symbol.'),
    webStyleBad ? 'point_symbol.web_style must be { style_name, name }' : undefined,
  )
}

function readPointSymbol(raw: Obj): StylePointSymbolRequest {
  const webStyle = isObj(raw.web_style)
    ? { styleName: str(raw.web_style.style_name)!, name: str(raw.web_style.name)! }
    : undefined

  return defined({
    kind: oneOf(POINT_KINDS, raw.kind),
    iconUrl: str(raw.icon_url),
    modelUrl: str(raw.model_url),
    libraryModel: str(raw.library_model),
    webStyle,
    primitive: str(raw.primitive),
    primitiveColor: str(raw.primitive_color),
    size: num(raw.size),
    heading: num(raw.heading),
    sizeField: clearable(raw.size_field),
    headingField: clearable(raw.heading_field),
    placement: oneOf(PLACEMENT_3D, raw.placement),
    heightField: clearable(raw.height_field),
    heightUnit: oneOf(UNITS, raw.height_unit),
  })
}

/**
 * Reads the snake_case `style_layer` input into a request. Type-checks only: ranges, field
 * existence and engine fit are the planner's job. `null` when there is no usable layer id.
 */
export function readStyleRequest(input: unknown): StyleLayerRequest | null {
  if (!isObj(input)) return null
  const layerId = typeof input.layer_id === 'string' ? input.layer_id.trim() : ''
  if (layerId === '') return null

  const request: StyleLayerRequest = { layerId }
  if (input.target === 'layer' || input.target === 'slide') request.target = input.target
  const invalid: Partial<Record<Part, string>> = {}

  if (input.symbology !== undefined) {
    const { value, note } = readSymbology(input.symbology)
    if (value) request.symbology = value
    if (note) invalid.symbology = note
  }

  const simple: Array<[Part, (o: Obj) => object, (o: Obj) => string | undefined]> = [
    ['labels', readLabels, labelsProblem],
    ['extrusion', readExtrusion, extrusionProblem],
    ['elevation', readElevation, elevationProblem],
    ['pointSymbol', readPointSymbol, pointSymbolProblem],
  ]

  for (const [part, read, check] of simple) {
    const wire = part === 'pointSymbol' ? 'point_symbol' : part
    const raw = input[wire]
    if (raw === undefined) continue
    const problem = isObj(raw) ? check(raw) : `${wire} must be an object`

    if (problem) invalid[part] = problem
    else (request as unknown as Record<string, unknown>)[part] = read(raw as Obj)
  }

  if (Object.keys(invalid).length > 0) request.invalid = invalid

  return request
}
